/** @jsxImportSource ./simboluri-jsx */
// DRAWER-ul unui AX DE FUNDAȚIE — verificările axului pe care s-a dat click.
//
// A fost tabul „Fundație" al Studioului, cu desenul lui propriu. Din
// fundația nu are tab: în 3D e betonul de sub clădire, în 2D e
// un LAYER al planului structural („când trecem la varianta 2D
// fundația să fie unul din layere, să nu fie separată în alt tab"). Ce rămâne
// aici e drawer-ul: click pe un ax din desen ⇒ verificările LUI, lista
// simplificată cu expandare inline, ca la orice element. Tabloul complet stă
// în Verificări și în pagina axului.

import { cheiaFundatiei, fundatiaAdoptata } from "./fundatia";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import { useEffect, useMemo, useState } from "react";
import { AxFundatie, StepState } from "./tipuri";
import { useCatalogVerificari } from "./useCatalog";
import VerificareDetaliu from "./VerificareDetaliu";
import { BaraRE } from "./ElementPage";
import {
  VERDICT_CHIP,
  gasesteElement,
  indexElemente,
  nr,
} from "./elemente";
import { hashElement, hashSectiune } from "./legaturi";

type Lectura = { eticheta: Cheie; p: number; prag: number; re: number; combo: string };

/** Cea mai defavorabilă lectură a axului, peste toate combinațiile și ambele
 *  praguri obligatorii (NP 112 poate fi mai strict la centric). */
export function lecturaGuvernanta(ax: AxFundatie): Lectura | null {
  let cea: Lectura | null = null;
  for (const c of ax.combinatii ?? []) {
    const cand: Lectura[] = [];
    if (c.p1_kpa != null && c.prag_metoda_kpa)
      cand.push({
        eticheta: "struct.drax.lectura_metoda",
        p: c.p1_kpa,
        prag: c.prag_metoda_kpa,
        re: c.prag_metoda_kpa / c.p1_kpa,
        combo: c.combinatie,
      });
    if (c.p_max_np112_kpa != null && c.prag_np112_kpa)
      cand.push({
        eticheta: "struct.drax.lectura_np112",
        p: c.p_max_np112_kpa,
        prag: c.prag_np112_kpa,
        re: c.prag_np112_kpa / c.p_max_np112_kpa,
        combo: c.combinatie,
      });
    for (const l of cand) {
      if (l.p <= 0) continue;
      if (!cea || l.re < cea.re) cea = l;
    }
  }
  return cea;
}

export function verdictAx(ax: AxFundatie): "ok" | "bad" | "amb" {
  const okuri = [ax.ok_metoda, ax.ok_np112, ax.ok_desprindere, ax.ok_model];
  if (okuri.some((o) => o === false)) return "bad";
  const l = lecturaGuvernanta(ax);
  if (l && l.re < 1.1) return "amb";
  return "ok";
}


export default function DrawerAxFundatie({
  id,
  solutie,
  steps,
  ales,
  onAles,
}: {
  id: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
  // axul pe care s-a dat click în desen („Ax 4"); părintele randează drawer-ul
  // numai când există o selecție, deci aici nu e niciodată null
  ales: string;
  onAles: (ax: string | null) => void;
}) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const r = fundatiaAdoptata(steps) as any;
  const pasFundatie = cheiaFundatiei(steps);
  const verificari = r?.verificari;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const axe: AxFundatie[] = verificari?.axe ?? [];
  const [deschis, setDeschis] = useState<number | null>(null);
  const catalog = useCatalogVerificari();
  // închiderea unui ax închide și verificarea expandată în el
  useEffect(() => setDeschis(null), [ales]);

  // indexul de elemente conține și AXELE, cu verificările lor compuse — de
  // acolo vine lista din drawer, ca să fie identică cu cea din pagina axului
  const idx = useMemo(() => indexElemente(steps), [steps]);
  const t = useT();
  const elAx = useMemo(() => gasesteElement(idx, ales), [idx, ales]);

  const ax = axe.find((a) => a.nume === ales);
  if (!ax) {
    // axul cerut nu e (încă) verificat: Pasul 6 predimensionează, verificarea
    // pe reacțiunile reale vine după Pașii 7–12
    return (
      <aside className="ob-studio-inspector">
        <div className="ob-studio-sumar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <b className="ob-disp" style={{ fontSize: 17 }}>{ales}</b>
            <span style={{ flex: 1 }} />
            <button className="ob-studio-inchide" onClick={() => onAles(null)}
                    aria-label={t("struct.drax.inchide")}>×</button>
          </div>
          <div style={{ color: "var(--mut)", fontSize: 12, marginTop: 6 }}>
            {t(verificari?.stare === "asteptare"
              ? "struct.drax.doar_predim"
              : "struct.drax.dupa_p17")}{" "}
            <a href={hashSectiune(id, pasFundatie, solutie ?? null)}>
              {t("struct.drax.pagina_fundatiei")}
            </a>
          </div>
        </div>
      </aside>
    );
  }

  const v = verdictAx(ax);
  const chip = v === "bad"
    ? VERDICT_CHIP.neconform
    : v === "amb"
      ? VERDICT_CHIP.la_limita
      : VERDICT_CHIP.conform;
  const l = lecturaGuvernanta(ax);

  return (
    <aside className="ob-studio-inspector">
      <div className="ob-studio-sumar">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <b className="ob-disp" style={{ fontSize: 17 }}>{ax.nume}</b>
          <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}>
            {t("struct.drax.antet", {
              directie: ax.directie ?? t("struct.drax.ax"),
              stalpi: ax.nr_stalpi ?? "—",
            })}
          </span>
          <span style={{ flex: 1 }} />
          <span className={`ob-chip ${chip.cls}`} style={{ fontSize: 10 }}>{t(chip.text)}</span>
          <button className="ob-studio-inchide" onClick={() => onAles(null)}
                  aria-label={t("struct.drax.inchide")}>×</button>
        </div>
        <div className="sg-mono" style={{ fontSize: 11, color: "var(--mut)", marginTop: 5 }}>
          L {ax.L_m != null ? `${nr(ax.L_m, 1)} m` : "—"}
          {ax.B_m != null ? ` · B ${nr(ax.B_m, 2)} m` : ""}
          {ax.coef_excentricitate ? ` · k_exc ${nr(ax.coef_excentricitate, 2)}` : ""}
          {ax.latit_local ? t("struct.drax.latit") : ""}
          {ax.tasare_cm != null ? t("struct.drax.tasare", { n: nr(ax.tasare_cm, 1) }) : ""}
        </div>
        {l && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 7 }}>
            <span className="ob-lbl">{t(l.eticheta)}</span>
            <span className="sg-mono" style={{ fontSize: 12 }}>
              {nr(l.p, 1)} / ≤ {nr(l.prag, 1)} kPa
            </span>
            <BaraRE re={l.re} latime={70} />
          </div>
        )}
        <div style={{ marginTop: 7 }}>
          <a href={hashElement(id, ax.nume, solutie)} style={{ fontSize: 11 }}>
            {t("struct.drax.pagina_axului")}
          </a>
        </div>
      </div>

      <div style={{ padding: "12px 16px" }}>
        <div className="ob-lbl" style={{ marginBottom: 6 }}>
          {t("struct.drax.verificarile")}
        </div>
        {elAx?.verificari?.length ? (
          <table className="ob-sol-tabel ob-ver-lista">
            <tbody>
              {elAx.verificari.map((vv, i) => {
                const norma = vv.verdict.startsWith("SE VERIFICA")
                  ? "conform"
                  : vv.verdict.startsWith("ATENTIE")
                    ? "la_limita"
                    : "neconform";
                const c = VERDICT_CHIP[norma as keyof typeof VERDICT_CHIP];
                return (
                  <>
                    <tr key={i} onClick={() => setDeschis(deschis === i ? null : i)}
                        className={deschis === i ? "deschis" : undefined}>
                      <td className="ob-ver-sageata" style={{ width: 18 }}>
                        {deschis === i ? "▾" : "▸"}
                      </td>
                      <td style={{ fontSize: 12 }}>{vv.nume}</td>
                      <td style={{ width: 92 }}><BaraRE re={vv.RE} latime={40} /></td>
                      <td style={{ width: 74 }}>
                        <span className={`ob-chip ${c.cls}`} style={{ fontSize: 10 }}>{t(c.text)}</span>
                      </td>
                    </tr>
                    {deschis === i && (
                      <tr className="ob-ver-rand-detaliu">
                        <td />
                        <td colSpan={3}>
                          <VerificareDetaliu
                            v={vv}
                            pas={pasFundatie}
                            catalog={catalog}
                            linkPas={hashSectiune(id, pasFundatie, solutie ?? null, { ref: ax.nume })}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="sg-muted" style={{ fontSize: 12 }}>
            {t("struct.drax.in_tablou")}
            <a href={hashSectiune(id, "verificari", solutie ?? null)}>
              {t("struct.drax.verificari_link")}
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
